import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getOrdersTableData, OrdersTableEntry } from '@app/api/order.api';
import { Pagination, Tag } from 'api/table.api';
import { useTranslation } from 'react-i18next';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { Status } from '@app/components/profile/profileCard/profileFormNav/nav/payments/paymentHistory/Status/Status';
import { InputRef, TableColumnType } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { defineColorByPriority } from '@app/utils/utils';
import { useMounted } from '@app/hooks/useMounted';

type DataIndex = keyof OrdersTableEntry;

const initialPagination: Pagination = {
  current: 1,
  pageSize: 20,
};

const Card = styled(BaseCard)`
  margin-bottom: 3rem;
`;

export const OrdersTable: React.FC = () => {
  const [tableData, setTableData] = useState<{ data: OrdersTableEntry[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const searchInput = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMounted } = useMounted();

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getOrdersTableData(pagination).then((res) => {
        if (isMounted.current) {
          setTableData({ data: res.data, pagination: res.pagination, loading: false });
        }
      });
    },
    [isMounted],
  );

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch]);

  const handleSearch = (confirm: FilterDropdownProps['confirm']) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void, confirm: FilterDropdownProps['confirm']) => {
    clearFilters();
    confirm();
  };

  const handleTableChange = (pagination: Pagination) => {
    fetch(pagination);
  };

  const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<OrdersTableEntry> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <BaseInput
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={(selectedKeys[0] as string | number) ?? ''}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(confirm)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <BaseSpace>
          <BaseButton type="primary" onClick={() => handleSearch(confirm)} icon={<SearchOutlined />} size="small">
            Search
          </BaseButton>
          <BaseButton
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </BaseButton>
        </BaseSpace>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()) || false,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns: ColumnsType<OrdersTableEntry> = [
    {
      key: 'fullName',
      title: t('common.fullName'),
      dataIndex: 'fullName',
      ...getColumnSearchProps('fullName'),
    },
    {
      key: 'mobile',
      title: t('common.mobile'),
      dataIndex: 'mobile',
      ...getColumnSearchProps('mobile'),
    },
    {
      key: 'address',
      title: t('common.address'),
      dataIndex: 'address',
    },
    {
      key: 'date',
      title: t('common.date'),
      dataIndex: 'date',
      sorter: (a: OrdersTableEntry, b: OrdersTableEntry) => a.date - b.date,
      render: (date: number) => <>{new Date(date).toLocaleDateString('fa-IR')}</>,
    },
    {
      key: 'status',
      title: t('common.status'),
      dataIndex: 'status',
      width: '10%',
      filters: [
        {
          text: 'در صف چاپ',
          value: 0,
        },
        {
          text: 'در حال ارسال',
          value: 1,
        },
        {
          text: 'ارسال شده',
          value: 2,
        },
        {
          text: 'کنسل شده',
          value: 3,
        },
        {
          text: 'انجام شده',
          value: 4,
        },
      ],
      onFilter: (value: boolean | Key, record: OrdersTableEntry) => record.status.code == value,
      render: (status: Tag) => <Status color={defineColorByPriority(status.priority)} text={status.value} />,
    },
    {
      key: 'totalPrice',
      title: t('common.totalPrice'),
      dataIndex: 'totalPrice',
      width: '10%',
      render: (totalPrice: number) => (
        <>{totalPrice === 0 ? t('common.free') : Intl.NumberFormat('fa-IR').format(totalPrice)}</>
      ),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      dataIndex: 'actions',
      width: '10%',
      render: (_, record: { key: string }) => (
        <BaseButton type="primary" ghost onClick={() => navigate(`/orders/${record.key}`)}>
          {t('common.moreDetails')}
        </BaseButton>
      ),
    },
  ];

  return (
    <Card title={t('common.orders')}>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={tableData.pagination}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        bordered
      />
    </Card>
  );
};
